import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { BlankViewImageVariants, BlankView } from 'src/Components/blank-view'
import { useSystemDetailsTabs } from 'src/Libs/service-dashboard/hooks/use-system-details-tabs'
import SystemInformation from './SystemInformation'
import { useDetailedInstallationData } from '../../hooks/use-detailed-installation-data'
import { useInstallerData } from '../../hooks/use-installer-data'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { makeStyles } from 'tss-react/mui'
import HardwareTests from './hardware-tests/HardwareTests'
import SystemDecommissionedBanner from '../decomission-banner/SystemDecommissionedBanner'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import PaperContainer from '../PaperContainer'
import { EmsProductType } from 'src/Routes/Installation/InstallationModel'
import QntmList from '../hardware-components/QntmList'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { Installation } from 'src/Libs/installations'

const useStyles = makeStyles()(theme => ({
  container: {
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
}))

/**
 * Wrapper component that holds information about a specific system/storage inside an installation
 */
const SystemDetails: React.FC = () => {
  const { classes } = useStyles()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const [installation] = useDetailedInstallationData(installationId)
  const { storage, storageLoading, storageError, deviceLinks, storageDeviceLink } = useActiveInstallationContext()
  const { t } = useCustomTranslation()
  const installationAccessType = useInstallationAccessType(installation as Installation)
  const isPlannedInstallation = installationAccessType === 'e3dc-setup-configurator-access'

  const [installerUser, installerUserLoading] = useInstallerData(storage?.installerId)
  const { setActiveTab } = useSystemDetailsTabs(installationId, storageId)
  const navigate = useNavigate()

  const isDataFetching = storageLoading || installerUserLoading

  useEffect(() => {
    setActiveTab(0)
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPlannedInstallation) {
      navigate(`/installation/${installationId}/configuration/general`, { replace: true })
    }
  }, [isPlannedInstallation, navigate, installationId])

  if (isDataFetching)
    return (
      <Box>
        <EditorSkeleton />
      </Box>
    )

  if (!storage || storageError)
    return (
      <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" mt={4}>
        <BlankView
          imageVariant={BlankViewImageVariants.Worried}
          title={'errors.pageNotFound.messageTitle'}
          explanation={'errors.pageNotFound.message'}
        />
      </Box>
    )

  return (
    <Box className={classes.container}>
      <BannerQueue location={BannerLocations.SystemDetails} />
      <SystemDecommissionedBanner storage={storageDeviceLink} installationId={installationId!} />
      <SystemInformation
        installationId={installationId!}
        installation={installation}
        storage={storage}
        installerUser={installerUser}
        storageId={storageId}
        deviceLinks={deviceLinks}
      />
      {storage?.product === EmsProductType.qntm && (
        <PaperContainer title={t('system-details.components.qntm.title')}>
          <QntmList installationId={installationId} deviceLinkId={storageDeviceLink?.id} />
        </PaperContainer>
      )}
      <HardwareTests />
    </Box>
  )
}

export default SystemDetails
